import React, { useRef, useEffect, useState, useContext } from "react";
import appContext from '../AppContext';
import { vectorBasemapLayer } from "esri-leaflet-vector";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import "leaflet.markercluster";
import mapConfig from '../config/mapConfig.json';
import { getBoundaryPolygons } from "../services/BoundaryService";
import { useTranslation } from "react-i18next";


function Map() {
  const mapRef = useRef(null);
  const isRenderedRef = useRef(null);
  const [map, setMap] = useState(null);
  const [schoolLayer, setSchoolLayer] = useState(null);
  const { schools, searchedPlace, boundaryPolys, setBoundaryPolys, setSelectedSchool } = useContext(appContext);
  const [ clickActive, setClickActive ] = useState([]);
  const [ showPolys, setShowPolys ] = useState(false);
  const esColor = "#E04833";
  const msColor = "#8FC91F";
  const hsColor = "#1C778B";
  const [esLayer, setEsLayer] = useState(null);
  const [msLayer, setMsLayer] = useState(null);
  const [hsLayer, setHsLayer] = useState(null);
  // set grades for boundary search on school click
  const esGrades = ["pk_3","pk_4","k","1","2","3","4","5"];
  const msGrades = ["6","7","8"];
  const hsGrades = ["9","10","11","12"];
  const [markerLayer, setMarkerLayer] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (mapRef.current !== null && !isRenderedRef.current) {
      // Add a basemap
      const streets = vectorBasemapLayer("ArcGIS:Streets", {
        apiKey: mapConfig.arcgisKey,

      });
      const tempMap = L.map(mapRef.current, {
        attributionControl: false,
        zoomControl: false,
        layers: [streets],
        center: [mapConfig.centerPoint.lat, mapConfig.centerPoint.long],
        zoom: mapConfig.zoomLevel,
        maxZoom: 20
      });

      L.control.zoom({
        position: 'bottomright'
      }).addTo(tempMap);

      setNoAddressMap(tempMap);
      setMap(tempMap);
      isRenderedRef.current = true;
    }

  // eslint-disable-next-line
  }, []);

  // on schools update
  useEffect(() => {
    if (searchedPlace != null && map != null) {
      // if exists - delete
      if (markerLayer != null) {
        map.removeLayer(markerLayer);
      }

      // create new address point layer
      let newMakerLayer = L.geoJSON(searchedPlace, {
        onEachFeature: (feature = {}, layer) => {
          const { properties = {} } = feature;
          layer.bindTooltip(`<p className='text-bold'>${properties.FullAddress || properties.Alias}</p>`, { closeButton: false, offset: L.point(0, 0) });
        },
        pointToLayer: function(feature, latlng) {
            // create marker
            return L.marker(latlng, {
              icon: L.divIcon({
                className: '',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
                html: `<svg aria-label="${feature.properties.FullAddress}" xmlns="http://www.w3.org/2000/svg" fill="#E64534" viewBox="0 0 24 24" stroke-width="1.5" stroke="#E64534" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>`
              })
            });
          }
        }).addTo(map);

      // set centerpoint and zoom
      map.setView(new L.LatLng(searchedPlace.properties.Latitude,searchedPlace.properties.Longitude), 14);
      setMarkerLayer(newMakerLayer);
    } else if (searchedPlace == null && map != null) {
      setNoAddressMap(map);
      setBoundaryPolys([]);
    }
  // eslint-disable-next-line
  }, [searchedPlace]);

  useEffect(() => {
    // when context schools value changes -- if greater than 0 and map has loaded
    if (schools.length > 0 && map != null) {
      // add schools
      if (schoolLayer != null) {
        map.removeLayer(schoolLayer);
      }
      let validSchools = schools.filter(item => {
        return item.geometry.coordinates[0] !== 0;
      })
      let newSchoolLayer = L.geoJSON(validSchools, {
      onEachFeature: (feature = {}, layer) => {
        const { properties = {} } = feature;

        layer.bindTooltip(`<p className='text-bold'>${properties.name}</p>`, { closeButton: false, offset: L.point(20, -33) });
        layer.on({
          click: (evt) => {
            let feat = evt.target.feature;
            if (!feat.properties.hide_boundaries) {
              // school types
              let layerTypes = [];
              let llLocationObj = {lat: feat.geometry.coordinates[1], lon: feat.geometry.coordinates[0]};
              if (feat.properties.grades_served.some(r=> esGrades.indexOf(r) >= 0)) {
                layerTypes.push("ES");
              }
              if (feat.properties.grades_served.some(r=> msGrades.indexOf(r) >= 0)) {
                layerTypes.push("MS");
              }
              if (feat.properties.grades_served.some(r=> hsGrades.indexOf(r) >= 0)) {
                layerTypes.push("HS");
              }
              let boundaryPoly = getBoundaryPolygons(llLocationObj, layerTypes)
              boundaryPoly.then(data => {
                setBoundaryPolys(data);
                setShowPolys(true);
              });
            } else {
              setBoundaryPolys([]);
            }
            setSelectedSchool(feat.properties.id);
          }
        })
      },
      //onEachFeature: onEachFeature,
      pointToLayer: function(feature, latlng) {
          // Directly return the Marker for the given `latlng`
          let textColor = "#117789";
          if (!parseInt(feature.rank)) {
            textColor = "red";
          }
          return L.marker(latlng, {
            icon: L.divIcon({
              className: '',
              iconSize: [48, 60],
              iconAnchor: [24, 58],
              html: `<svg  aria-label="School marker for ${feature.rank}" width="48" height="54" viewBox="0 0 48 54">
              <polygon points="41,32 24,52 7,32" style="fill:#117789;stroke:#117789;stroke-width:1" />
              <circle cx="24"
                      cy="22"
                      r="20"
                      stroke="#117789"
                      stroke-width="2"
                      fill="white"/>
              <circle cx="24"
                      cy="22"
                      r="16"
                      stroke="#117789"
                      stroke-width="2"
                      fill="white"/>
              
              <text font-size="14"
                    stroke=${textColor}
                    font-family="Arial"
                    text-anchor="middle"
                    alignment-baseline="baseline"
                    stroke-width="1"
                    x="24"
                    y="26">${feature.rank}</text>
              </svg>
              `
            })
          });
        }
      });
      //.addTo(map);
      var markers = L.markerClusterGroup({
        maxClusterRadius: function(zoom) { return 3; },
        zoomToBoundsOnClick: false,
        iconCreateFunction: function(cluster) {
          return L.divIcon({ html: '<div><span>+</span></div>', className: 'marker-cluster marker-cluster-small', iconSize: new L.Point(40, 40) });
        }
      });
      markers.addLayer(newSchoolLayer);
      map.addLayer(markers);
      setSchoolLayer(markers);
    } else if (schools.length === 0 && map != null) {
      // remove all markers
      if (schoolLayer != null) {
        map.removeLayer(schoolLayer);
      }
    }
  // eslint-disable-next-line
  }, [schools]);

  // on boundaries update
  useEffect(() => {
    // clear out polygons
    setEsLayer(null);
    setMsLayer(null);
    setHsLayer(null);
    setShowPolys(false);
    setClickActive([]);
    if (map != null) {
      // remove current boundary layers
      let layers = [esLayer, msLayer, hsLayer];
      layers.forEach(layer => {
        if (layer != null) {
          map.removeLayer(layer);
        }
      });
    }
  // eslint-disable-next-line
  }, [boundaryPolys]);


  // on boundaries update
  useEffect(() => {
    // if boundary polys changed by map click - show boundary
    // otherwise hide until wanting to be shown
    if (showPolys) {
      let types = boundaryPolys.filter(returnObj => {
        return returnObj.status === 'fulfilled';
      }).map(obj => obj.value.layerType);
      types.forEach(type => {
        setVisState(type, true, true);
      });
    }
    // eslint-disable-next-line
  }, [boundaryPolys, showPolys]);

  const setNoAddressMap = (mapVar) => {
    // if exists - delete
    if (markerLayer != null) {
      mapVar.removeLayer(markerLayer);
    }

    // create new marker layer for midpoint
    let newMakerLayer = L.marker([mapConfig.centerPoint.lat, mapConfig.centerPoint.long], {
      icon: L.divIcon({
        className: '',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        html: `<svg aria-label="U.S. Capitol West Terraces and Steps" xmlns="http://www.w3.org/2000/svg" fill="#E64534" viewBox="0 0 24 24" stroke-width="1.5" stroke="#E64534" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>`
      })
    }).bindTooltip(`<p className='text-bold'>U.S. Capitol West Terraces and Steps</p>`, { closeButton: false, offset: L.point(0, 0) }).addTo(mapVar);

    // set centerpoint and zoom
    mapVar.setView(new L.LatLng(mapConfig.centerPoint.lat, mapConfig.centerPoint.long), 14);
    setMarkerLayer(newMakerLayer);
  }

  // polygon boundary functions section
  function addRemovePolys(show, layerVar, layerType, colorVar) {
    // just incase its showing - remove
    if (layerVar != null) {
      map.removeLayer(layerVar);
    }
    let newLayer = null;
    if (show) {
      // find matching boundary layer
      if (boundaryPolys.length > 0) {
        newLayer = L.geoJson(null, {
          style: {
            color: colorVar,
            weight: 5,
            fillOpacity: 0
          }
        })
        let polys = boundaryPolys.filter(returnObj => {
          return returnObj.status === 'fulfilled' && returnObj.value.layerType === layerType;
        }).map(obj => obj.value.data);
        polys.forEach(poly => {
          newLayer.addData(poly);
        })
        newLayer.addTo(map);
      }
    }
    return newLayer;
  }

  function setVisState(layerType, show, set) {
    // check present
    let checkLayers = boundaryPolys.filter(returnObj => {
      return returnObj.status === 'fulfilled' && returnObj.value.layerType === layerType;
    });
    if (checkLayers.length === 0) {
      return;
    }
    
    // if hover but already included in click
    if (!set && clickActive.includes(layerType)){
      return;
    }
    // otherwise set up add/remove
    let layer = null;
    switch (layerType){
      case "ES": 
        layer = addRemovePolys(show, esLayer, layerType, esColor);
        setEsLayer(layer);
        break;
      case "MS":
        layer = addRemovePolys(show, msLayer, layerType, msColor);
        setMsLayer(layer);
        break;
      case "HS":
        layer = addRemovePolys(show, hsLayer, layerType, hsColor);
        setHsLayer(layer);
        break;
      default:
        console.log('No set type');
        break;
    }
    
    // handle click events - add type into an array
    let clickArr = clickActive;
    if (set && show) {
      // if not there add it
      if (!clickArr.includes(layerType)) clickArr.push(layerType);
    } else if (set && !show) {
      // if there - remove it
      clickArr = clickArr.filter(item => item !== layerType)
    }
    setClickActive(clickArr); // set state
  }

  return <div className="absolute inset-0 flex flex-col overflow-hidden items-center print:border print:border-[#97afb4]">
    <div className="min-w-[1500px] w-full h-full" ref={ mapRef }></div>
    <div className="h-11 bg-white hidden lg:flex flex-row justify-between w-full align-middle">
      <div className="text-[10px] w-28 my-auto pl-2">
        {t('map.rollover')}
      </div>
      <span className="text-[8px] flex grow justify-around">
        <div className={"flex my-auto items-center cursor-pointer " + (esLayer != null ? "text-black" : "text-gray-400")}
           onClick={() => setVisState("ES", !clickActive.includes("ES"), true)} onMouseEnter={() => setVisState("ES", true, false)} onMouseLeave={() => setVisState("ES", false, false)}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke={(esLayer != null ? esColor : "#a0a0a0")} className="w-8 h-8">
            <path strokeLinecap="butt" d="M19.5 12h-15" />
          </svg>
          <span>{t('map.es')}</span>
        </div>
        <div className={"flex my-auto items-center cursor-pointer " + (msLayer != null ? "text-black" : "text-gray-400")} 
            onClick={() => setVisState("MS", !clickActive.includes("MS"), true)} onMouseEnter={() => setVisState("MS", true, false)} onMouseLeave={() => setVisState("MS", false, false)}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke={(msLayer != null ? msColor : "#a0a0a0")} className="w-8 h-8">
            <path strokeLinecap="butt" d="M19.5 12h-15" />
          </svg>
          <span className="text-black">{t('map.ms')}</span>
        </div>
        <div className={"flex my-auto items-center cursor-pointer " + (hsLayer != null ? "text-black" : "text-gray-400")}
            onClick={() => setVisState("HS", !clickActive.includes("HS"), true)} onMouseEnter={() => setVisState("HS", true, false)} onMouseLeave={() => setVisState("HS", false, false)}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke={(hsLayer != null ? hsColor : "#a0a0a0")} className={"w-8 h-8"}>
            <path strokeLinecap="butt" d="M19.5 12h-15" />
          </svg>
          <span className="h-full">{t('map.hs')}</span>
        </div>
      </span>
      <div className="text-[10px] w-40 my-auto pr-2">
      {t('map.moreinfo')} <a className="text-my-emerald" href="http://www.myschooldc.org/faq/faqs#bound" target="_blank" rel="noreferrer">{t('map.clickhere')}</a>
      </div>
      {
        boundaryPolys.length === 0 &&
          <div className="absolute inset-0 bg-gray-600 opacity-50"></div>
      }
    </div>
  </div>

}

export default Map;